import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type { WsEventEnvelope } from '../../../lib/websocket/events';

/**
 * D13 — espelho vivo da Tela 2: liga os WS events da conversa observada à
 * invalidação React Query, filtrando pelo `conversationId` alvo.
 *
 * - `conversation.attendant-composing` (dirigido) → invalida `['composer-state', id]`.
 * - `conversation.message.received` → invalida `['messages', id]` + `['conversation', id]`.
 * - `conversation.message.delivery.updated` → invalida `['messages', id]`.
 * - `conversation.status.changed` / `conversation.operation_mode.changed` → invalida `['conversation', id]`.
 *
 * T2-2 (ferramentas) — quando há `coAttendanceId`:
 * - `coaching-message.created` (dirigido, filtra por `coAttendanceId`) → invalida `['coaching-messages', id]`.
 * - `supervisor-session-note.auto-saved` (dirigido, filtra por `coAttendanceId`) → invalida `['session-note', id]`.
 * - `coattendance.discrepancy-detected` (dirigido, filtra por `conversationId`) → invalida `['discrepancy-indicator', convId]`.
 *
 * R15: os payloads de coaching/session-note carregam `coAttendanceId`; o de
 * discrepância carrega `conversationId` (websocket.types.ts do engine).
 */
export function useAcompanhamentoWsSync(
  conversationId: string | undefined,
  coAttendanceId?: string | null,
): void {
  const qc = useQueryClient();
  const { subscribe } = useWebSocket();
  useEffect(() => {
    if (conversationId === undefined) return;

    const matches = (envelope: WsEventEnvelope<{ conversationId?: string }>): boolean =>
      envelope.data.conversationId === conversationId;

    const invalidateComposer = (envelope: WsEventEnvelope<{ conversationId?: string }>): void => {
      if (!matches(envelope)) return;
      void qc.invalidateQueries({ queryKey: ['composer-state', conversationId] });
    };
    const invalidateMessages = (envelope: WsEventEnvelope<{ conversationId?: string }>): void => {
      if (!matches(envelope)) return;
      void qc.invalidateQueries({ queryKey: ['messages', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
    };
    const invalidateMessagesOnly = (
      envelope: WsEventEnvelope<{ conversationId?: string }>,
    ): void => {
      if (!matches(envelope)) return;
      void qc.invalidateQueries({ queryKey: ['messages', conversationId] });
    };
    const invalidateConversation = (
      envelope: WsEventEnvelope<{ conversationId?: string }>,
    ): void => {
      if (!matches(envelope)) return;
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
    };

    // Discrepância é dirigida por `conversationId` (regra do silêncio — adversarial).
    const invalidateDiscrepancy = (
      envelope: WsEventEnvelope<{ conversationId?: string }>,
    ): void => {
      if (!matches(envelope)) return;
      void qc.invalidateQueries({ queryKey: ['discrepancy-indicator', conversationId] });
    };

    const offs = [
      subscribe(WS_EVENTS.CONVERSATION_ATTENDANT_COMPOSING, invalidateComposer),
      subscribe(WS_EVENTS.MESSAGE_RECEIVED, invalidateMessages),
      subscribe(WS_EVENTS.MESSAGE_DELIVERY_UPDATED, invalidateMessagesOnly),
      subscribe(WS_EVENTS.CONVERSATION_STATUS_CHANGED, invalidateConversation),
      subscribe(WS_EVENTS.OPERATION_MODE_CHANGED, invalidateConversation),
      subscribe(WS_EVENTS.COATTENDANCE_DISCREPANCY_DETECTED, invalidateDiscrepancy),
    ];

    // Coaching + session-note são dirigidos por `coAttendanceId` (canal isolado).
    if (coAttendanceId !== undefined && coAttendanceId !== null) {
      const matchesCoAtt = (envelope: WsEventEnvelope<{ coAttendanceId?: string }>): boolean =>
        envelope.data.coAttendanceId === coAttendanceId;
      const invalidateCoaching = (envelope: WsEventEnvelope<{ coAttendanceId?: string }>): void => {
        if (!matchesCoAtt(envelope)) return;
        void qc.invalidateQueries({ queryKey: ['coaching-messages', coAttendanceId] });
      };
      const invalidateSessionNote = (
        envelope: WsEventEnvelope<{ coAttendanceId?: string }>,
      ): void => {
        if (!matchesCoAtt(envelope)) return;
        void qc.invalidateQueries({ queryKey: ['session-note', coAttendanceId] });
      };
      offs.push(
        subscribe(WS_EVENTS.COACHING_MESSAGE_CREATED, invalidateCoaching),
        subscribe(WS_EVENTS.SUPERVISOR_SESSION_NOTE_AUTO_SAVED, invalidateSessionNote),
      );
    }

    return () => {
      for (const off of offs) off();
    };
  }, [qc, subscribe, conversationId, coAttendanceId]);
}
